import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { mapChrome, mapChromeBase, mapIconButton } from "../../../components/ui/mapChrome";

interface Props {
  map: google.maps.Map | null;
  /** Abierto desde fuera (menú Herramientas → "Buscar direcciones"). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Esperar a que se deje de escribir: cada predicción es una llamada a Places.
const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;
const MAX_RESULTS = 5;

// Buscador de direcciones del mapa. Cerrado es sólo una lupa del tamaño y
// estilo de los controles de Google (junto a pantalla completa), así no tapa
// el mapa. Abierto, una barra; al elegir un resultado se deja un pin como en
// Google Maps, que se quita con la X.
//
// Reemplaza al panel "Buscar Dirección" (384 px con título y párrafo, sin
// marcador) que abría el menú de herramientas.
export default function AddressSearch({ map, open, onOpenChange }: Props) {
  const { t } = useTranslation("home");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [active, setActive] = useState(0);
  const [searched, setSearched] = useState(false);
  const [placed, setPlaced] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const service = useRef<google.maps.places.AutocompleteService | null>(null);
  // Una sesión agrupa las predicciones + el detalle elegido en un solo cobro
  // de Places, en vez de uno por letra escrita.
  const session = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const info = useRef<google.maps.InfoWindow | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const removeMarker = useCallback(() => {
    info.current?.close();
    marker.current?.setMap(null);
    marker.current = null;
    setPlaced(null);
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    info.current?.close();
    marker.current?.setMap(null);
  }, []);

  // Clic fuera: se cierra la lista; si no hay pin ni texto, también la barra.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setResults([]);
      if (!query && !placed) onOpenChange(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, query, placed, onOpenChange]);

  const predict = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < MIN_CHARS) {
      setResults([]);
      setSearched(false);
      return;
    }
    timer.current = setTimeout(() => {
      if (!window.google?.maps?.places) return;
      service.current ??= new google.maps.places.AutocompleteService();
      session.current ??= new google.maps.places.AutocompleteSessionToken();
      const bounds = map?.getBounds();
      service.current.getPlacePredictions(
        {
          input: q,
          sessionToken: session.current,
          // Prefiere lo que está a la vista (no lo exige): "Juárez" en
          // Monterrey antes que Ciudad Juárez.
          ...(bounds ? { locationBias: bounds } : {}),
        },
        (preds, status) => {
          setSearched(true);
          setActive(0);
          setResults(
            status === google.maps.places.PlacesServiceStatus.OK && preds ? preds.slice(0, MAX_RESULTS) : [],
          );
        },
      );
    }, DEBOUNCE_MS);
  };

  const select = (p: google.maps.places.AutocompletePrediction) => {
    if (!map) return;
    const svc = new google.maps.places.PlacesService(map);
    svc.getDetails(
      {
        placeId: p.place_id,
        fields: ["geometry", "name", "formatted_address"],
        sessionToken: session.current ?? undefined,
      },
      (place, status) => {
        session.current = null; // la sesión termina con el detalle
        const loc = place?.geometry?.location;
        if (status !== google.maps.places.PlacesServiceStatus.OK || !loc) return;
        // Una ciudad o colonia trae su recuadro: encuadrarla. Una dirección
        // exacta no: acercar a nivel calle.
        if (place.geometry?.viewport && !p.types?.includes("street_address") && !p.types?.includes("premise")) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.panTo(loc);
          map.setZoom(17);
        }
        const label = p.structured_formatting.main_text;
        marker.current?.setMap(null);
        marker.current = new google.maps.Marker({ map, position: loc, title: place.formatted_address || label });
        info.current ??= new google.maps.InfoWindow();
        const div = document.createElement("div");
        div.style.cssText = "font: 13px system-ui, sans-serif; color:#111; max-width:240px";
        const strong = document.createElement("strong");
        strong.textContent = label;
        const addr = document.createElement("div");
        addr.style.cssText = "color:#555; margin-top:2px";
        addr.textContent = place.formatted_address || p.structured_formatting.secondary_text || "";
        div.append(strong, addr);
        info.current.setContent(div);
        marker.current.addListener("click", () => info.current?.open({ map, anchor: marker.current! }));
        setQuery(label);
        setResults([]);
        setPlaced(label);
      },
    );
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    setSearched(false);
    removeMarker();
    inputRef.current?.focus();
  };

  const { isDarkMode } = useDarkMode();
  const c = mapChrome(isDarkMode);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-label={t("addressSearch.open", "Buscar dirección")}
        title={t("addressSearch.open", "Buscar dirección")}
        className={`absolute top-3 right-[52px] z-20 ${mapIconButton(isDarkMode)}`}
      >
        <i className="ri-search-line text-[17px]" aria-hidden />
      </button>
    );
  }

  return (
    <div ref={rootRef} className="absolute top-3 right-[52px] z-30 w-[min(320px,calc(100%-80px))] motion-safe:animate-fadeIn">
      <div className={`h-8 flex items-center rounded-[10px] ${mapChromeBase} ${c.surface}`}>
        <i className={`ri-search-line text-[15px] pl-2.5 pr-2 ${c.muted}`} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="address-search-results"
          aria-label={t("addressSearch.placeholder", "Buscar dirección o lugar")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            predict(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && results[active]) select(results[active]);
            if (e.key === "Escape") {
              if (results.length) setResults([]);
              else if (!query && !placed) onOpenChange(false);
              else clear();
            }
          }}
          placeholder={t("addressSearch.placeholder", "Buscar dirección o lugar")}
          className={`flex-1 min-w-0 h-full bg-transparent text-[13px] outline-none ${isDarkMode ? "text-white placeholder-gray-500" : "text-gray-900 placeholder-gray-400"} [&::-webkit-search-cancel-button]:appearance-none`}
        />
        <button
          type="button"
          onClick={query || placed ? clear : () => onOpenChange(false)}
          aria-label={query || placed ? t("addressSearch.clear", "Quitar búsqueda y pin") : t("addressSearch.close", "Cerrar")}
          title={query || placed ? t("addressSearch.clear", "Quitar búsqueda y pin") : t("addressSearch.close", "Cerrar")}
          className={`size-8 grid place-items-center ${c.muted} ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
        >
          <i className="ri-close-line text-base" aria-hidden />
        </button>
      </div>

      {results.length > 0 && (
        <ul id="address-search-results" role="listbox" className={`mt-1.5 p-1 overflow-hidden rounded-xl ${mapChromeBase} ${c.surface}`}>
          {results.map((r, i) => (
            <li
              key={r.place_id}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(r)}
              className={`flex items-start gap-2 px-2.5 py-2 rounded-lg cursor-pointer ${i === active ? (isDarkMode ? "bg-white/[0.07]" : "bg-black/[0.04]") : ""}`}
            >
              <i className={`ri-map-pin-2-line text-base mt-0.5 ${c.muted}`} aria-hidden />
              <span className="min-w-0">
                <span className={`block text-[13px] truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}>{r.structured_formatting.main_text}</span>
                <span className={`block text-xs truncate ${c.muted}`}>{r.structured_formatting.secondary_text}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {searched && results.length === 0 && query.trim().length >= MIN_CHARS && !placed && (
        <p className={`mt-1.5 px-3 py-2 text-xs rounded-xl ${mapChromeBase} ${c.surface} ${c.muted}`}>{t("addressSearch.noResults", "Sin resultados")}</p>
      )}
    </div>
  );
}
